import React from 'react';
import { X, Clock, Award, Monitor } from 'lucide-react';

export const TrainingModal = ({ isOpen, onClose, training, language }) => {
  if (!isOpen || !training) return null;

  const t = {
    tr: { close: 'Kapat', duration: 'Süre', level: 'Seviye', format: 'Format' },
    uz: { close: 'Yopish', duration: 'Davomiyligi', level: 'Daraja', format: 'Format' },
    ru: { close: 'Закрыть', duration: 'Длительность', level: 'Уровень', format: 'Формат' },
    en: { close: 'Close', duration: 'Duration', level: 'Level', format: 'Format' }
  };

  const labels = t[language] || t.en;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-center justify-center p-4 animate-fade-in overflow-y-auto">
      <div 
        className="glass-card max-w-4xl w-full p-8 md:p-12 relative rounded-2xl shadow-glow-xl animate-scale-in my-8"
        style={{ boxShadow: '0 0 12px rgba(0, 224, 255, 0.2)' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-tertiary hover:text-brand-teal-light transition-all duration-300 hover:rotate-90 p-2 rounded-lg hover:bg-bg-tertiary z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4 pr-12">
            {training.title}
          </h2>
          
          {/* Badges */}
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-brand-teal/20 text-brand-teal-light rounded-full border border-brand-teal/30">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">{labels.duration}: {training.duration}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-brand-teal/20 text-brand-teal-light rounded-full border border-brand-teal/30">
              <Award className="w-4 h-4" />
              <span className="text-sm font-medium">{labels.level}: {training.level}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-brand-teal/20 text-brand-teal-light rounded-full border border-brand-teal/30">
              <Monitor className="w-4 h-4" />
              <span className="text-sm font-medium">{labels.format}: {training.format || 'Live + Lab'}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6 text-text-secondary leading-relaxed max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar">
          {training.fullDescription.split('\n\n').map((paragraph, index) => {
            // Check if it's a heading (contains bold markers or is short)
            const isHeading = paragraph.includes(':') && paragraph.split(':')[0].length < 50;
            
            if (isHeading) {
              const [heading, ...content] = paragraph.split(':');
              return (
                <div key={index} className="space-y-2">
                  <h3 className="text-xl font-semibold text-brand-teal-light">{heading.trim()}:</h3>
                  {content.length > 0 && (
                    <p className="text-text-secondary whitespace-pre-line">{content.join(':').trim()}</p>
                  )}
                </div>
              );
            }
            
            // Check if it's a list item (starts with bullet or dash)
            if (paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-')) {
              const items = paragraph.split('\n').filter(item => item.trim());
              return (
                <ul key={index} className="space-y-2 pl-4">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-brand-teal-light rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-text-secondary">{item.replace(/^[•\-]\s*/, '')}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            
            return (
              <p key={index} className="text-text-secondary whitespace-pre-line leading-relaxed">
                {paragraph.trim()}
              </p>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="mt-8 pt-6 border-t border-border-medium">
          <a
            href="https://forms.gle/c1SduPx1vdLyDp2U7"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex w-full md:w-auto justify-center"
            onClick={onClose}
          >
            {language === 'tr' ? 'Eğitime Kayıt Ol' : 
             language === 'uz' ? "Ta'limga Ro'yxatdan O'ting" : 
             language === 'ru' ? 'Записаться на обучение' : 
             'Register for Training'}
          </a>
        </div>
      </div>
    </div>
  );
};
